import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { AveragePlayerScore } from '__generated__/globalTypes';
import { Vertical } from 'atoms/layout/flex';
import { LabelS } from 'atoms/typography';

import { PlayerAppearance_anyPlayer } from './__generated__/index.graphql';

const Progress = styled.div`
  width: 100%;
  height: 2px;
  border-radius: var(--unit);
  background: var(--bg-color);
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: calc(100% - var(--progress));
    background: var(--progress-color);
    border-radius: var(--double-unit);
  }
`;

type Props = {
  player: PlayerAppearance_anyPlayer;
  mode:
    | AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE
    | AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE
    | AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE;
};

export const PlayerAppearance = ({ player, mode }: Props) => {
  const appearances = {
    LAST_TEN_PLAYED_SO5_AVERAGE_SCORE: (player.lastTenSo5Appearances || 0) / 10,
    LAST_FIVE_SO5_AVERAGE_SCORE: (player.lastFiveSo5Appearances || 0) / 5,
    LAST_FIFTEEN_SO5_AVERAGE_SCORE:
      (player.lastFifteenSo5Appearances || 0) / 15,
  }[mode];

  return (
    <Vertical gap={0.5}>
      <LabelS color="var(--c-nd-600)">
        <FormattedNumber value={appearances} style="percent" />
      </LabelS>
      <Progress
        style={
          {
            '--bg-color': 'var(--c-nd-200)',
            '--progress-color': 'var(--c-brand-300)',
            '--progress': `${appearances * 100}%`,
          } as CSSProperties
        }
      />
    </Vertical>
  );
};

PlayerAppearance.fragments = {
  anyPlayer: gql`
    fragment PlayerAppearance_anyPlayer on AnyPlayerInterface {
      slug
      lastFiveSo5Appearances
      lastTenSo5Appearances
      lastFifteenSo5Appearances
    }
  ` as TypedDocumentNode<PlayerAppearance_anyPlayer>,
};
